import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, ChevronDown, Flag } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { projectStatusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { NO_SUPPORT, PROJECT_STATUSES, type ProjectStatusView } from '../lib/project-status';
import { Badge, Button, Spinner, statusTone, useToast } from './ui';

/** Where the menu opens, and how tall its list may be (undefined: as tall as it is). */
interface Placement {
  side: 'start' | 'end';
  up: boolean;
  listHeight?: number;
}

const DOWN_FROM_START: Placement = { side: 'start', up: false };
/** Space kept between the menu and the screen's edge. */
const MARGIN = 12;
/** The list never shrinks below about three statuses; past that the page scrolls. */
const MIN_LIST = 180;

/** The top and bottom of the nearest scrolling box around `el` (the page's main area), on screen. */
function scrollArea(el: HTMLElement): { top: number; bottom: number } {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      const box = node.getBoundingClientRect();
      return { top: Math.max(0, box.top), bottom: Math.min(window.innerHeight, box.bottom) };
    }
  }
  return { top: 0, bottom: window.innerHeight };
}

/**
 * A project's status changed without its edit page (operator, 2026-10-09: "let
 * me change status with no edit separate button don't removed from edit keep
 * it"); the edit form keeps its own Status field.
 *
 * A menu, not a form: one choice, saved the moment it is made, like the top
 * bar's shift menu — nothing is typed, so the no-popups rule does not apply.
 * Each status says what it means for tasks and tickets. Completed is not
 * offered while a contract runs: the project is in support until it ends, and
 * the API would settle it there anyway. Support with no contract running is
 * support by hand.
 */
export function ProjectStatusMenu({ project }: { project: ProjectStatusView }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Placement>(DOWN_FROM_START);
  const boxRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const support = project.support ?? NO_SUPPORT;
  const running = support.contracts.length > 0;
  const contracts = support.contracts.map((c) => c.number).join(', ');
  const date = formatDate(support.until, locale);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      boxRef.current?.querySelector('button')?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // It opens from the button's start edge, or from its end where that would run
  // off the screen; downwards, or upwards where there is more room; and its list
  // scrolls when the screen is too short for all eight statuses.
  useLayoutEffect(() => {
    if (!open || !boxRef.current || !menuRef.current || !listRef.current) return;
    const button = boxRef.current.getBoundingClientRect();
    const rtl = getComputedStyle(boxRef.current).direction === 'rtl';
    const across = rtl ? button.right : window.innerWidth - button.left;
    const header = menuRef.current.offsetHeight - listRef.current.offsetHeight;
    const natural = header + listRef.current.scrollHeight;
    // Measured inside the page's scrolling area, which starts below the top bar:
    // what pokes out above it is cut off. The menu sits 6px (mt-1.5) off the button.
    const area = scrollArea(boxRef.current);
    const below = area.bottom - button.bottom - 6 - MARGIN;
    const above = button.top - area.top - 6 - MARGIN;
    const up = natural > below && above > below;
    const room = up ? above : below;
    setPlace({
      side: across >= menuRef.current.offsetWidth + 16 ? 'start' : 'end',
      up,
      listHeight: natural > room ? Math.max(MIN_LIST, room - header) : undefined,
    });
  }, [open]);

  const change = useMutation({
    mutationFn: (status: string) => api.patch<{ status: string }>(`/projects/${project.id}`, { status }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      // What it became: Completed while a contract runs settles in Support.
      toast.success(t('projects.statusChanged', { status: projectStatusLabel(saved.status, locale) }));
      setOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const meaning = (status: string): string => {
    if (status === 'SUPPORT') {
      return running ? t('projects.meaning.SUPPORT', { contracts, date }) : t('projects.meaning.SUPPORT_BY_HAND');
    }
    if (status === 'COMPLETED' && running) return t('projects.meaning.COMPLETED_LOCKED', { contracts, date });
    return t(`projects.meaning.${status}` as TranslationKey);
  };

  const now =
    project.status === 'SUPPORT'
      ? running
        ? `${projectStatusLabel(project.status, locale)} · ${t('projects.supportUntil', { date })}`
        : `${projectStatusLabel(project.status, locale)} · ${t('projects.byHand')}`
      : projectStatusLabel(project.status, locale);

  return (
    <div ref={boxRef} className="relative">
      <Button
        icon={<Flag className="h-4 w-4" aria-hidden />}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          // Measured afresh each time it opens, from its natural size.
          if (!open) setPlace(DOWN_FROM_START);
          setOpen(!open);
        }}
      >
        {t('projects.changeStatus')}
        <ChevronDown className="h-3.5 w-3.5 text-[var(--text-tertiary)]" aria-hidden />
      </Button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('projects.changeStatus')}
          className={clsx(
            'absolute z-dropdown w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md surface shadow-lg',
            place.up ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            place.side === 'start' ? 'start-0' : 'end-0',
          )}
        >
          <p className="border-b hairline px-3 py-2.5 text-caption text-muted">{t('projects.statusNow', { status: now })}</p>
          <div ref={listRef} className="overflow-y-auto p-1.5" style={{ maxHeight: place.listHeight }}>
            {PROJECT_STATUSES.map((status) => {
              const current = status === project.status;
              const locked = status === 'COMPLETED' && running && !current;
              const pending = change.isPending && change.variables === status;
              // Closing a project does not stop its contracts: Finance ends them.
              const contractGoesOn = running && !current && (status === 'CANCELLED' || status === 'ARCHIVED');
              return (
                <button
                  key={status}
                  type="button"
                  role="menuitemradio"
                  aria-checked={current}
                  disabled={current || locked || change.isPending}
                  onClick={() => change.mutate(status)}
                  className={clsx(
                    'flex w-full items-start gap-2.5 rounded-sm px-2.5 py-2 text-start transition-colors duration-fast',
                    'enabled:hover:bg-[var(--surface-sunken)] disabled:cursor-default',
                    current && 'bg-primary/6',
                    locked && 'opacity-60',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <Badge tone={statusTone(status)} dot>
                      {projectStatusLabel(status, locale)}
                    </Badge>
                    <span className="mt-1 block text-caption text-muted">{meaning(status)}</span>
                    {contractGoesOn && (
                      <span className="mt-0.5 block text-caption text-warning-fg dark:text-warning-dark">
                        {t('projects.meaning.contractRuns', { contracts, date })}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                    {pending ? (
                      <Spinner className="h-4 w-4" />
                    ) : current ? (
                      <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden />
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
